import { add, endOfDay, startOfDay } from 'date-fns'
import { useCallback, useEffect, useMemo } from 'react'

import { DateRangeValue } from '../../../components/DateRangePicker/DateRangePicker'
import { useLiveEventsTrackerAccessCheck } from '../../account/hooks/roleHooks'
import { LiveEventGroupBy } from '../utils/liveEventExamplesGrouping'
import { useLiveEventExamplesSearchParams } from './useLiveEventExamplesSearchParams'

/**
 * URL-backed state shared by the two Live Event Examples surfaces (the
 * Implementation Examples page and the Game page subtab): the date range,
 * defaulted to the last three months on first visit, and the group-by and
 * hide-discontinued toggles.
 */
export const useLiveEventExamplesControls = () => {
  const hasAccessToBlueprints = useLiveEventsTrackerAccessCheck()
  const { setSearchParams, parsedParams } = useLiveEventExamplesSearchParams({ hasAccessToBlueprints })

  // Fills in only the missing bound, once on mount; a shared link's own range wins.
  useEffect(() => {
    if (parsedParams.dateFrom == null || parsedParams.dateTo == null) {
      const defaultStart = startOfDay(add(new Date(), { months: -3 })).getTime()
      const defaultEnd = endOfDay(new Date()).getTime()
      setSearchParams((current) => ({
        ...current,
        dateFrom: current.dateFrom ?? defaultStart,
        dateTo: current.dateTo ?? defaultEnd,
      }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleGroupByChange = useCallback(() => {
    setSearchParams((current) => ({
      ...current,
      groupBy: current.groupBy === LiveEventGroupBy.Blueprint ? LiveEventGroupBy.Type : LiveEventGroupBy.Blueprint,
    }))
  }, [setSearchParams])

  const handleHideDiscontinuedBlueprintsChange = useCallback(() => {
    setSearchParams((current) => ({
      ...current,
      hideDiscontinuedBlueprints: !current.hideDiscontinuedBlueprints,
    }))
  }, [setSearchParams])

  const handleDateRangeChange = useCallback(
    (value?: DateRangeValue) => {
      setSearchParams((current) => ({
        ...current,
        dateFrom: value?.fromDate?.getTime(),
        dateTo: value?.toDate?.getTime(),
      }))
    },
    [setSearchParams]
  )

  const dateRange = useMemo(
    () => ({
      fromDate: parsedParams.dateFrom ? new Date(parsedParams.dateFrom) : undefined,
      toDate: parsedParams.dateTo ? new Date(parsedParams.dateTo) : undefined,
    }),
    [parsedParams.dateFrom, parsedParams.dateTo]
  )

  return {
    hasAccessToBlueprints,
    parsedParams,
    setSearchParams,
    groupBy: parsedParams.groupBy,
    hideDiscontinuedBlueprints: parsedParams.hideDiscontinuedBlueprints,
    dateRange,
    handleGroupByChange,
    handleHideDiscontinuedBlueprintsChange,
    handleDateRangeChange,
  }
}

export type LiveEventExamplesControls = ReturnType<typeof useLiveEventExamplesControls>
